import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, NgZone, Input } from '@angular/core';
import { FormStateService } from '../../services/form-state.service';
import { FormBuilderService } from '../../services/form-builder.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ThisReceiver } from '@angular/compiler';
import { BreadcrumbService   } from 'xng-breadcrumb';


export interface FormState {
    name: string;
    label: string;
    logo?: string;
    cover_image?: string;
    header_content?: string;
    footer_content?: string;
    form_type: string;
    form_fields: FormSection[];
}

export interface FormSection {
    section_id: number;
    section_label: string;
    section_form_fields: FormField[];
}

interface FormModel {
  readOnly: boolean;
  fieldId: string;
  fieldValue: string;
  fieldOrder: number;
  fieldName: string;
  fieldDescription: string;
  isMandatory: boolean;
  formName: string;
  displaySection: string;
  placeHolder: string;
  fieldType: string;
  fieldOptions: any[];
  fileProperities: FieldProperties;
  answer: any[];
  isValid: boolean;
  visible: boolean;
  groupName?: string;
  disabled?: boolean;
  dependant?: {
    dependantOn: string;
    dependantValues: any[];
  };
}

export interface FieldProperties {
    file_format?: string[];
    file_max_size?: string;
  }

export interface FormField {
      id: number;
      name: string;
      type: string;
      label: string;
      description: string;
      order_no: number;
      is_required: boolean;
      placeholder: string | null;
      element_type: 'field' | 'group';
      options?: any[];
      form_fields?: FormField[];
      properties?: FieldProperties;
}

@Component({
    selector: 'app-form-preview',
    templateUrl: './form-preview.component.html',
    styleUrls: ['./form-preview.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormPreviewComponent implements OnInit, OnDestroy {
    @Input() previewMode: boolean = false;
    @Input() formData: any = null;

    formType: string = '';
    isLoading: boolean = true;
    stateEmpty: boolean = false;
    errorMessage: string = '';
    errorType: 'not-found' | 'server-error' | 'invalid-uuid' | null = null;
    private subscription = new Subscription();
    private broadcastChannel: BroadcastChannel;
    private lastUpdate: number = 0;

    templateCollections: Array<Array<any>> = [];
    headerContent: string = '';
    footerContent: string = '';
    formLogo: string = '';
    formCoverImage: string = '';
    formName: string = '';

    constructor(
        private formStateService: FormStateService,
        private formBuilderService: FormBuilderService,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private breadcrumbService: BreadcrumbService  
    ) {
        this.broadcastChannel = new BroadcastChannel('form_state_broadcast');

        this.broadcastChannel.onmessage = (event) => {
            if (this.useStateBasedPreview()) {
                this.ngZone.run(() => {
                    if (event.data.type === 'STATE_CLEAR') {
                        this.formData = null;
                        this.cdr.markForCheck();
                    } else if (event.data.type === 'STATE_UPDATE') {
                        this.isLoading = true;
                        this.formData = event.data.state;
                        this.lastUpdate = event.data.state.lastUpdated;
                        this.transformFormFields(this.formData);
                        this.isLoading = false;
                        this.cdr.markForCheck();
                    }
                });
            }
        };
    }

    private useStateBasedPreview(): boolean {
        const params = new URLSearchParams(window.location.search);
        return params.get('uuid') === 'null' || !params.get('uuid');
    }

    ngOnInit() {
        if (this.previewMode && this.formData) {
            this.loadFormData(this.formData);
            return;
        }

        this.route.queryParams.subscribe(params => {
            const uuid = params['uuid'];
            this.isLoading = true;

            if (uuid === 'null' || !uuid) {
                this.subscribeToFormState();
            } else {
                this.loadFormFromApi(uuid);
            }
        });
    }

    updateBreadcrumb(): void {
        this.breadcrumbService.set('@formPreview', `${this.formName} Preview`);
    }
    

    private transformFormFields(formData: FormState): void {
        this.templateCollections = [];
        this.headerContent = formData.header_content || '';
        this.footerContent = formData.footer_content || '';
        this.formLogo = formData.logo || '';
        this.formCoverImage = formData.cover_image || '';
        this.formName = formData.name || '';
        this.formType = formData.form_type || '';

        const formName = this.formName;

        formData.form_fields.forEach((section: FormSection) => {
            const sectionFields: FormModel[] = [];
            
            section.section_form_fields?.forEach((field: FormField) => {
              if (field.element_type === 'field') {
                sectionFields.push(this.createFieldModel(field, section, formName));
              } else if (field.element_type === 'group' && field.form_fields) {
                if (field.name === 'primary_number' || field.name === 'primary_number' || field.name === 'parent_number') {
                  const filteredFields = field.form_fields.filter(f => f.name !== 'country_code');
                  filteredFields.forEach((groupField: FormField) => {
                    sectionFields.push(this.createFieldModel(groupField, section, formName, field.name));
                  });
                } else {
                  field.form_fields.forEach((groupField: FormField) => {
                    sectionFields.push(this.createFieldModel(groupField, section, formName, field.name));
                  });
                }
              }
            });
      
            if (sectionFields.length > 0) {
              this.templateCollections.push(sectionFields);
            }
        });
        this.updateBreadcrumb();
    }

    private createFieldModel(field: FormField, section: FormSection, formName: string, groupName?: string): FormModel {
        const model: FormModel = {
          fieldId: field.id.toString(),
          fieldOrder: field.order_no,
          fieldName: field.label,
          fieldValue: field.name,
          fieldDescription: field.description,
          isMandatory: field.is_required,
          formName: formName,
          displaySection: section.section_label,
          placeHolder: field.placeholder || '',
          fieldType: field.type.toLowerCase(),
          fieldOptions: field.options || [],
          answer: [],
          isValid: true,
          readOnly: true,
          disabled: false,
          visible: true,
          fileProperities: {
            file_format: field.properties?.file_format || [],
            file_max_size: field.properties?.file_max_size || '2MB'
          },
        };
    
        if (groupName) {
          model.groupName = groupName;
        }
        return model;
      }

    private loadFormData(data: FormState | null) {
        if (data) {
            this.formData = data;
            this.transformFormFields(data);
        }
        this.isLoading = false;
        this.cdr.markForCheck();
    }

    private subscribeToFormState() {
        this.subscription.add(
            this.formStateService.getFormState()
                .pipe(
                    distinctUntilChanged((prev, curr) => 
                        JSON.stringify(prev) === JSON.stringify(curr)
                    )
                )
                .subscribe({
                    next: (state) => {
                        this.isLoading = false;
                        if (!state || !state.form_fields || state.form_fields.length === 0) {
                            this.stateEmpty = true;
                            this.formData = null;
                        } else if (state.lastUpdated && state.lastUpdated > this.lastUpdate) {
                            this.stateEmpty = false;
                            this.loadFormData(state);
                            this.lastUpdate = state.lastUpdated;
                        }
                        this.cdr.markForCheck();
                    },
                    error: (error) => {
                        this.isLoading = false;
                        this.handleError(error);
                        this.cdr.markForCheck();
                    }
                })
        );
    }

    private loadFormFromApi(uuid: string) {
        this.subscription.add(
            this.formBuilderService.getFormFastApi(uuid).subscribe({
                next: (response) => {
                    if (response?.body) {
                        this.loadFormData(response.body);
                    } else {
                        this.handleInvalidState();
                    }
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    this.isLoading = false;
                    this.handleApiError(error);
                    this.cdr.markForCheck();
                }
            })
        );
    }

    private handleApiError(error: any) {
        this.formData = null;
        
        if (error.status === 404) {
            this.errorType = 'not-found';
            this.errorMessage = 'Form not found. The form you are looking for does not exist.';
        } else if (error.status === 400) {
            this.errorType = 'invalid-uuid';
            this.errorMessage = 'Invalid form ID provided.';
        } else {
            this.errorType = 'server-error';
            this.errorMessage = 'Internal server error. Please try again later.';
        }

        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.errorMessage
        });
    }

    private handleError(error: any) {
        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load form preview'
        });
    }

    private handleInvalidState() {
        this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Invalid form state'
        });
    }

    trackByFieldFn(index: number, field: any): string | number {
        return field.id || index;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.broadcastChannel.close();
    }

    backToEdit() {
        this.router.navigate(['/forms/create']);
    }

    backToForms() {
        this.router.navigate(['/forms']);
    }

}
